import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Heart,
  ImagePlus,
  Video,
  Send,
  Loader2,
  Trash2,
  MessageSquareOff,
  X,
} from "lucide-react";
import { toast } from "sonner";

function PostMedia({ media }) {
  if (!media || media.length === 0) return null;
  return (
    <div
      className={`grid gap-2 mt-3 ${
        media.length === 1 ? "grid-cols-1" : "grid-cols-2"
      }`}
    >
      {media.map((m, i) => (
        <div
          key={i}
          className="rounded-xl overflow-hidden border border-border bg-muted"
        >
          {m.type === "video" ? (
            <video
              src={m.data}
              controls
              className="w-full max-h-[480px] bg-black"
            />
          ) : (
            <img
              src={m.data}
              alt=""
              className="w-full max-h-[520px] object-cover"
            />
          )}
        </div>
      ))}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function PostCard({ post, isAdmin, onLike, onDelete }) {
  return (
    <Card data-testid={`post-${post.id}`} className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-black/5 shrink-0">
            {post.author_photo ? (
              <img
                src={post.author_photo}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full dp-gradient-bg flex items-center justify-center text-white text-xs font-semibold">
                {(post.author_name || "A").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">
              {post.author_name || "Admin"}
            </div>
            <div className="text-xs text-muted-foreground">
              {timeAgo(post.created_at)}
            </div>
          </div>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(post)}
              data-testid={`post-delete-${post.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <h3 className="font-display font-bold text-lg leading-tight">
          {post.title}
        </h3>
        {post.content && (
          <p className="mt-1.5 text-sm text-foreground/90 whitespace-pre-wrap">
            {post.content}
          </p>
        )}
        <PostMedia media={post.media} />

        <div className="mt-4 flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onLike(post)}
            data-testid={`post-like-${post.id}`}
            className={post.liked_by_me ? "text-rose-600" : ""}
          >
            <Heart
              className={`h-4 w-4 mr-1.5 ${
                post.liked_by_me ? "fill-rose-600" : ""
              }`}
            />
            {post.likes_count} {post.likes_count === 1 ? "like" : "likes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Composer({ onPosted }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const imgRef = useRef(null);
  const vidRef = useRef(null);

  const handleFiles = (e, kind) => {
    const list = Array.from(e.target.files || []);
    const limit = kind === "video" ? 12 : 6;
    for (const f of list) {
      if (f.size > limit * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${limit}MB`);
        return;
      }
    }
    setFiles((prev) => [...prev, ...list]);
    e.target.value = "";
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!title.trim()) {
      toast.error("Please add a title");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("content", content.trim());
      files.forEach((f) => fd.append("media", f));
      await api.post("/admin/posts", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Update posted");
      setTitle("");
      setContent("");
      setFiles([]);
      onPosted();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Post failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="post-composer">
      <CardContent className="p-5">
        <form onSubmit={submit} className="space-y-3">
          <Input
            placeholder="Announcement title…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="composer-title-input"
            className="text-base font-semibold border-0 focus-visible:ring-0 px-0"
          />
          <Textarea
            placeholder="Share an update with the team — what's going on?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            data-testid="composer-content-input"
            rows={3}
            className="resize-none border-0 focus-visible:ring-0 px-0"
          />

          {files.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {files.map((f, i) => (
                <div
                  key={i}
                  className="relative aspect-square rounded-lg overflow-hidden bg-muted border border-border"
                >
                  {f.type.startsWith("video/") ? (
                    <div className="h-full w-full flex flex-col items-center justify-center text-xs p-2 text-center">
                      <Video className="h-6 w-6 text-muted-foreground" />
                      <span className="mt-1 line-clamp-2">{f.name}</span>
                    </div>
                  ) : (
                    <img
                      src={URL.createObjectURL(f)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                    data-testid={`composer-remove-file-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-1">
              <input
                ref={imgRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e, "image")}
                data-testid="composer-image-input"
              />
              <input
                ref={vidRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => handleFiles(e, "video")}
                data-testid="composer-video-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => imgRef.current?.click()}
                data-testid="composer-add-image-btn"
              >
                <ImagePlus className="h-4 w-4 mr-1.5 text-emerald-600" /> Image
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => vidRef.current?.click()}
                data-testid="composer-add-video-btn"
              >
                <Video className="h-4 w-4 mr-1.5 text-blue-600" /> Video
              </Button>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="composer-submit-btn"
              className="dp-gradient-bg text-white hover:opacity-90"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1.5" /> Post update
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default function ActivityFeed() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletePost, setDeletePost] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/posts");
      setPosts(data);
    } catch {
      toast.error("Failed to load feed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleLike = async (post) => {
    // Optimistic
    setPosts((prev) =>
      prev.map((p) =>
        p.id === post.id
          ? {
              ...p,
              liked_by_me: !p.liked_by_me,
              likes_count: p.likes_count + (p.liked_by_me ? -1 : 1),
            }
          : p
      )
    );
    try {
      await api.post(`/posts/${post.id}/like`);
    } catch {
      toast.error("Couldn't update like");
      refresh();
    }
  };

  const confirmDelete = async () => {
    if (!deletePost) return;
    try {
      await api.delete(`/admin/posts/${deletePost.id}`);
      toast.success("Post removed");
      setDeletePost(null);
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"
      );
    }
  };

  return (
    <div className="min-h-screen dp-grain" data-testid="activity-feed-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 dp-fade-in space-y-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            Activity
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Team feed
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            All the latest updates from the company in one place.
          </p>
        </div>

        {isAdmin && <Composer onPosted={refresh} />}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : posts.length === 0 ? (
          <Card data-testid="empty-feed">
            <CardContent className="py-16 flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-2xl bg-muted flex items-center justify-center">
                <MessageSquareOff className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-3 font-semibold">No updates yet</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {isAdmin
                  ? "Be the first to post something to your team."
                  : "Check back soon for company news."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {posts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                isAdmin={isAdmin}
                onLike={handleLike}
                onDelete={setDeletePost}
              />
            ))}
          </div>
        )}
      </main>

      <AlertDialog
        open={!!deletePost}
        onOpenChange={(o) => !o && setDeletePost(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this post?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the post for everyone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="post-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
